import { useEffect, useRef, useState } from "react";

export function useCacheResult<T>(
  cacheKey: string,
  fetchFunction: Function,
  functionParams: (T | undefined)[] = [],
  useEffectParams: (T | undefined)[] = [],
  canExecute = true,
) {
  const cache = useRef<{ [key: string | symbol]: any }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!fetchFunction || !canExecute) {
      return;
    }

    const hasInvalidFunctionParam = functionParams?.some(
      (param) => param === undefined || (typeof param === "number" && Number.isNaN(param)),
    );

    if (hasInvalidFunctionParam) {
      return;
    }

    const fetchData = async () => {
      if (cache.current[cacheKey]) {
        return;
      }

      try {
        setIsLoading(true);
        const data = await fetchFunction(...(functionParams?.length ? functionParams : []));
        cache.current[cacheKey] = data;
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...useEffectParams, canExecute]);

  return { isLoading, data: cache.current[cacheKey] };
}
